import { DragDropModule } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ResizableDirective, ResizeEvent, ResizeHandleDirective } from 'angular-resizable-element';
import { DialogComponent } from '../dialog.service';
import { computeTargetSize, isResizeWithinBounds } from './resize.helpers';

interface DialogWrapperData<T extends DialogComponent> {
  originalData: any;
  originalComponent: T;
  resizable?: boolean;
}

@Component({
  selector: 'vectronic-dialog-wrapper',
  templateUrl: './dialog-wrapper.component.html',
  styleUrls: ['./dialog-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatTooltipModule,
    DragDropModule,
    ResizableDirective,
    ResizeHandleDirective,
  ],
})
export class DialogWrapperComponent<T extends DialogComponent> implements OnInit, OnDestroy {
  @ViewChild('contentHost', { read: ViewContainerRef, static: true })
  contentHost!: ViewContainerRef;
  dialogRef = inject(MatDialogRef);
  originalInstance: InstanceType<T>;

  private readonly minResizeWidthPx = 320;
  private readonly minResizeHeightPx = 160;

  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  private data = inject(MAT_DIALOG_DATA) as DialogWrapperData<T>;
  private parentInjector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private componentRef?: ComponentRef<InstanceType<T>>;

  readonly resizable = !!this.data?.resizable;

  ngOnInit() {
    this.captureResizeStartSize();
    this.loadActualComponent();
  }

  private captureResizeStartSize() {
    const pane = this.hostElement.closest('.cdk-overlay-pane') as HTMLElement | null;
    const rect = (pane ?? this.hostElement).getBoundingClientRect();
    this.resizeStartWidth = Math.round(rect.width) || this.resizeStartWidth;
    this.resizeStartHeight = Math.round(rect.height) || this.resizeStartHeight;
  }

  private loadActualComponent() {
    this.contentHost.clear();
    const injector = Injector.create({
      providers: [
        {
          provide: MatDialogRef,
          useValue: this.dialogRef,
        },
        {
          provide: MAT_DIALOG_DATA,
          useValue: this.data.originalData,
        },
      ],
      parent: this.parentInjector,
    });
    this.componentRef = this.contentHost.createComponent<InstanceType<T>>(
      this.data.originalComponent as any,
      { injector },
    );
    this.originalInstance = this.componentRef?.instance as any;
    this.cdr.detectChanges();
  }

  onResizeStart({ rectangle }: ResizeEvent) {
    this.resizeStartWidth = Math.round(rectangle.width ?? 0);
    this.resizeStartHeight = Math.round(rectangle.height ?? 0);
  }

  validateResize = ({ rectangle }: ResizeEvent): boolean => {
    const width = computeTargetSize(rectangle.width, this.resizeStartWidth);
    const height = computeTargetSize(rectangle.height, this.resizeStartHeight);
    const viewportWidth = this.document.documentElement.clientWidth ?? 0;
    const viewportHeight = this.document.documentElement.clientHeight ?? 0;
    return isResizeWithinBounds(
      width,
      height,
      this.minResizeWidthPx,
      this.minResizeHeightPx,
      viewportWidth,
      viewportHeight,
    );
  };

  onResizing({ rectangle }: ResizeEvent) {
    this.applySizeToPane(rectangle);
  }

  onResizeEnd({ rectangle }: ResizeEvent) {
    this.applySizeToPane(rectangle);
  }

  private applySizeToPane(rectangle: ResizeEvent['rectangle']) {
    const pane = this.hostElement.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (!pane) {
      return;
    }
    const width = computeTargetSize(rectangle.width, this.resizeStartWidth);
    const height = computeTargetSize(rectangle.height, this.resizeStartHeight);
    if (width > 0) {
      pane.style.width = `${width}px`;
      pane.style.maxWidth = `${width}px`;
    }
    if (height > 0) {
      pane.style.height = `${height}px`;
      pane.style.maxHeight = `${height}px`;
    }
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }
}
